import { useMemo, useState } from 'react'
import {
  Alert, Box, Button, Group, NumberInput, Select, Stack, Switch,
  Table, Text, Tooltip,
} from '@mantine/core'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'
import { DataCard, EmptyState } from '@/components/ui'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
import {
  useSocCatalogo,
  useSocNaoClassificados,
  useSocPoliticas,
  useSalvarPoliticas,
} from '@/hooks/useSoc'
import type { SocPolicy } from '@/api/soc'
import { formatTimeAgo } from '@/utils/formatTimeAgo'
import { rotuloDoNivel, rotuloHistorico } from '@/pages/admin/socLabels'

const NIVEIS = [1, 2, 3] as const

/**
 * Coluna "Alerta" fixa na rolagem horizontal, e destaque de linha também no
 * teclado.
 *
 * Os dois precisam de seletor de pseudo-classe e de `:nth-of-type`, que o
 * `styles` do Mantine v7 (estilo inline) não expressa. Um `<style>` local é o
 * padrão já usado no repo para esse caso (`NetworkFlow.tsx`,
 * `JobStatusBadge.tsx`).
 *
 * A célula fixa precisa de fundo OPACO — sem ele o conteúdo rolado passa por
 * baixo e o nome do alerta fica ilegível. Por isso o fundo é repetido nos três
 * estados que a linha pode ter: normal (`--ai-card-bg`, DS §5.1 — é o fundo do
 * WidgetCard em que a tabela vive), listrada e sob o ponteiro/foco.
 *
 * `:focus-within` na `tr` é o par de teclado do `highlightOnHover`: tabulando
 * por 3 controles × N linhas, o anel de foco cai só no controle e a referência
 * de qual alerta se está editando se perde.
 *
 * O `:where()` da regra listrada não é enfeite — é o que faz a ordem funcionar.
 * Com `[data-striped]:nth-of-type(odd)` contando especificidade, a listra
 * ganhava do destaque de foco e a célula fixa ficava listrada enquanto o resto
 * da linha acendia. Medido no navegador. `:where()` zera esse peso, que é
 * exatamente como o próprio Mantine escreve a regra dele.
 */
const ESTILO_TABELA = `
.soc-alertas-tabela th[data-coluna-fixa],
.soc-alertas-tabela td[data-coluna-fixa] {
  position: sticky;
  inset-inline-start: 0;
  z-index: 1;
  background-color: var(--ai-card-bg);
  border-inline-end: 1px solid var(--ai-border-light);
}
.soc-alertas-tabela tbody > tr:where([data-striped='odd']:nth-of-type(odd)) td[data-coluna-fixa] {
  background-color: var(--table-striped-color);
}
.soc-alertas-tabela tbody > tr:hover td[data-coluna-fixa],
.soc-alertas-tabela tbody > tr:focus-within td[data-coluna-fixa] {
  background-color: var(--table-highlight-on-hover-color, var(--table-hover-color));
}
.soc-alertas-tabela tbody > tr:focus-within {
  background-color: var(--table-highlight-on-hover-color, var(--table-hover-color));
}
`

export function ConfiguracaoDeAlertas({ teamId }: { teamId: number }) {
  const catalogo = useSocCatalogo(teamId)
  const politicas = useSocPoliticas(teamId)
  const balde = useSocNaoClassificados(teamId)
  const salvar = useSalvarPoliticas(teamId)

  const [edicao, setEdicao] = useState<Record<string, SocPolicy>>({})

  const porKey = useMemo(() => {
    const base: Record<string, SocPolicy> = {}
    for (const p of politicas.data ?? []) base[p.catalog_key] = p
    return { ...base, ...edicao }
  }, [politicas.data, edicao])

  function mexer(key: string, patch: Partial<SocPolicy>) {
    setEdicao((e) => ({
      ...e,
      // Defaults primeiro, depois o que ja existe, depois a mudanca — nesta
      // ordem. Invertida, o default sobrescreveria a politica salva e toda
      // edicao de uma coluna zeraria as outras.
      [key]: {
        ...{
          catalog_key: key,
          entry_level: 1,
          escalate_after_min: null,
          reack_after_min: null,
          broadcast_all: false,
        },
        ...porKey[key],
        ...patch,
        catalog_key: key,
      },
    }))
  }

  const naoClassificados = balde.data ?? []
  const itens = catalogo.data ?? []
  // Quantas linhas o operador MEXEU — não quantas políticas existem. Ver o
  // comentário do botão, mais abaixo.
  const pendentes = Object.keys(edicao).length

  return (
    // DS §4.4: 32px é o degrau "entre secoes" — separa o bloco de avisos do
    // card da tabela. `gap="lg"` valia 20px, que não existe na escala do DS, e
    // dava aos avisos e à tabela o MESMO respiro, apagando a distinção.
    <Stack gap={32}>
      {/* DS §4.4: 12px é o degrau "entre campos relacionados" — os dois avisos
          são o mesmo bloco de contexto, e é assim que se lê que são um só. */}
      <Stack gap={12}>
        {/* O balde ficava FORA de qualquer tratamento de estado: `?? []`
            colapsava "carregando", "a leitura falhou" e "não há nenhum" no
            mesmo array vazio, e o aviso simplesmente não aparecia nos três
            casos. Ele existe justamente para a cobertura não se degradar em
            silêncio conforme as fontes evoluem — e era ele que se degradava em
            silêncio. `vazio` sem `estadoVazio` não renderiza nada: aqui o vazio
            é legítimo e não merece tela. */}
        <EstadoDeConsulta consulta={balde} vazio={naoClassificados.length === 0}>
          <Alert
            color="yellow"
            icon={<IconAlertTriangle size={18} />}
            // Não "sem configuração": o que falta é a CATEGORIA no catálogo, e
            // isso não se resolve nesta tela (é `soc_catalog.py`). Prometer a
            // ação aqui manda o operador procurar um controle que não existe.
            title={`${naoClassificados.length} tipo(s) de alerta chegaram sem categoria conhecida no catálogo`}
          >
            <Stack gap={4}>
              {naoClassificados.map((n) => (
                <Text key={n.catalog_key} size="sm">
                  <Text span ff="monospace">{n.catalog_key}</Text>
                  {' — '}
                  {n.hits} {n.hits === 1 ? 'ocorrência' : 'ocorrências'}, última{' '}
                  {formatTimeAgo(n.last_seen)}
                </Text>
              ))}
            </Stack>
          </Alert>
        </EstadoDeConsulta>

        <Alert color="inovareTeal" title="O que o alarme faz, e o que não faz">
          {/* DS §2.3: contexto informativo usa a cor da marca, não azul genérico. */}
          <Text size="sm">
            O nível diz quem <strong>pertence</strong> ao time, não quem está de turno:
            todo integrante do nível recebe. E o alarme é melhor esforço — ele acende a
            tela e vibra, mas não fura o modo silencioso nem o Não Perturbe do aparelho.
          </Text>
        </Alert>
      </Stack>

      {/* O `DataCard` fica FORA do `EstadoDeConsulta`, e o `EstadoDeConsulta`
          por dentro dele. A ordem importa: com o card dentro, todo ramo que não
          fosse o de sucesso apagava o título "Alertas e níveis" junto — e o
          título é a única coisa que diz ao operador em que card ele está.
          É por isso que ligar `vazio` na disposição anterior derrubava o teste
          de `AbaTimesDeSoc` que prova a abertura do detalhe pelo título.

          São DUAS consultas alimentando esta tabela. `catalogo` é a que produz
          as linhas, então é ela o `consulta`; `politicas` (que preenche nível,
          escalonamento e broadcast de cada linha) entra por `falhou` — sem
          isso, políticas em erro renderizariam a tabela inteira com as colunas
          desabilitadas, indistinguível de "nada configurado ainda", que é
          exatamente a afirmação que faria o operador reconfigurar por cima.
          `data === undefined` no teste porque falha de RECARGA não pode apagar
          dado bom da tela — é a mesma regra que o próprio EstadoDeConsulta
          aplica ao erro da consulta principal. */}
      <DataCard flush title="Alertas e níveis">
        <EstadoDeConsulta
          consulta={catalogo}
          falhou={politicas.isError && politicas.data === undefined}
          vazio={itens.length === 0}
          estadoVazio={
            // DS §6.9. O texto NÃO convida a configurar: `CATALOGO` é constante
            // de módulo em `soc_catalog.py`, igual para todo time, e nunca está
            // legitimamente vazio. Catálogo vazio aqui é defeito de leitura, e
            // dizer "cadastre seu primeiro alerta" seria mandar o operador
            // procurar um botão que não existe.
            <EmptyState
              bare
              size="md"
              accent="warning"
              icon={IconAlertTriangle}
              title="O catálogo de alertas veio vazio"
              description="O catálogo é o mesmo para todos os times — ele não nasce vazio e não se preenche por aqui. Vazio nesta tela significa que a leitura não trouxe os tipos de alerta, não que falte configurar algo. Se recarregar não resolver, é caso para a plataforma."
              secondaryAction={{
                label: 'Tentar de novo',
                icon: <IconRefresh size={14} />,
                onClick: () => catalogo.refetch(),
              }}
            />
          }
        >
          <style>{ESTILO_TABELA}</style>
          {/* O rótulo do nível escolhido vinha truncado ("Nível 3 – última
              esca…"): o operador via o número e perdia o PAPEL, que é a parte
              que decide. Só a coluna do seletor recebe largura — as outras
              quatro continuam distribuídas pelo navegador, nas proporções que
              já tinham. `minWidth` sobe junto porque, sem isso, a largura sai
              das vizinhas e a truncagem só muda de coluna: 900 é o 720 antigo
              mais o que o seletor passou a pedir (330 = "Nível 2 — recebe se
              ninguém reconhecer" a 14px, mais o padding e a seta do campo). */}
          <Table.ScrollContainer minWidth={900}>
            <Table className="soc-alertas-tabela" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th data-coluna-fixa>Alerta</Table.Th>
                  <Table.Th>Histórico</Table.Th>
                  <Table.Th w={330}>Entra no nível</Table.Th>
                  {/* A unidade sai do cabeçalho, não se repete nas N células. */}
                  <Table.Th>Escala após (min)</Table.Th>
                  {/* Rótulo deliberadamente DIFERENTE de "Escala após": os dois
                      são minutos e ficam lado a lado, e quem preenche o errado
                      às 3h acorda a pessoa errada. "Escala" sobe de nível;
                      "Religa" chama de novo QUEM JÁ RECONHECEU. */}
                  <Table.Th>Religa após (min)</Table.Th>
                  <Table.Th>Todos os níveis</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {itens.map((item) => {
                  const p = porKey[item.key]
                  const semNivel = !p
                  return (
                    <Table.Tr key={item.key}>
                      <Table.Td data-coluna-fixa>
                        <Text size="sm" fw={600}>{item.rotulo}</Text>
                        {/* O par configurado/não-configurado é a pergunta que a
                            tabela existe para responder, e estava dito só pelo
                            placeholder pálido do seletor — três colunas à
                            direita, fora da tela no celular. Aqui ele mora na
                            coluna fixa, que a rolagem nunca leva embora.
                            `--ai-text-secondary` (DS §5.1) e não a cor de
                            atenção: em texto pequeno o amarelo do DS §2.3 não
                            alcança o contraste de §11.1.

                            NA MESMA LINHA da chave, e não empilhado: como 14
                            das 19 linhas são não configuradas, uma terceira
                            linha de texto derrubou de ~14 para ~11 as linhas
                            visíveis, e a tarefa desta tela é configurar muitos
                            alertas de uma vez. Medido nas capturas da passada
                            de ofício de 31/08/2026. De quebra, a altura volta a
                            ser igual nas duas classes: alternar 52/68 px sem
                            agrupamento lia como serrilha, não como grupo. */}
                        <Text size="xs" c="var(--ai-text-secondary)" ff="monospace">
                          {item.key}
                          {semNivel && (
                            <Text span fw={600} c="var(--ai-text-secondary)" ff="var(--mantine-font-family)">
                              {' · Não configurado'}
                            </Text>
                          )}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {/* O selo era `Badge` sólido em caixa alta e, no estado
                            comum, IDÊNTICO em todas as linhas: a mancha mais
                            forte da tabela sem discriminar nada. Vira texto no
                            mesmo peso do outro ramo, com o ícone carregando a
                            cor de atenção — que num ícone não depende do
                            contraste exigido de texto. */}
                        {item.fonte_nunca_entregou ? (
                          <Group gap={4} wrap="nowrap">
                            <IconAlertTriangle
                              size={14}
                              color="var(--mantine-color-yellow-7)"
                              aria-hidden
                            />
                            <Text size="sm" c="var(--ai-text-secondary)">{rotuloHistorico(item)}</Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="var(--ai-text-secondary)">{rotuloHistorico(item)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Select
                          aria-label={`Nível de entrada de ${item.rotulo}`}
                          // Diz a AÇÃO, não só o estado — mesmo precedente do
                          // "já possui time — abra o time existente na lista".
                          placeholder="Não dispara — escolha um nível"
                          value={p ? String(p.entry_level) : null}
                          onChange={(v) =>
                            v && mexer(item.key, { entry_level: Number(v) })
                          }
                          data={NIVEIS.map((n) => ({
                            value: String(n),
                            label: rotuloDoNivel(n),
                          }))}
                        />
                      </Table.Td>
                      <Table.Td>
                        {/* O motivo do desabilitado não era descobrível: o
                            campo dizia o ESTADO ("não escala"), e nada dizia
                            que ele volta a funcionar ao escolher o nível.
                            O `Tooltip` precisa do `Box`: controle desabilitado
                            não dispara evento de ponteiro. */}
                        <Tooltip
                          label="Escolha o nível de entrada para habilitar o escalonamento"
                          disabled={!semNivel}
                        >
                          <Box>
                            <NumberInput
                              aria-label={`Minutos para escalar ${item.rotulo}`}
                              placeholder="Não escala"
                              min={1}
                              max={1440}
                              value={p?.escalate_after_min ?? ''}
                              onChange={(v) =>
                                mexer(item.key, {
                                  escalate_after_min: v === '' ? null : Number(v),
                                })
                              }
                              disabled={semNivel}
                              // DS §3.2: valor numérico é dado, e dado é
                              // JetBrains Mono. `tabular-nums` para os minutos
                              // alinharem coluna a coluna entre as linhas.
                              // Só quando HÁ número: a monoespaçada aplicada ao
                              // campo vazio caía também sobre o placeholder, e
                              // "Não escala" é palavra, não dado — na conferência
                              // visual foi o que mais destoou.
                              styles={{
                                input: {
                                  fontFamily:
                                    p?.escalate_after_min != null
                                      ? 'var(--mantine-font-family-monospace)'
                                      : undefined,
                                  fontVariantNumeric: 'tabular-nums',
                                },
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        {/* DESABILITADO onde a fonte não emite recuperação.
                            Campo que aceita e ignora é pior que campo ausente:
                            o valor ficaria gravado e `_prazo_de_reack` nunca o
                            armaria, e ninguém descobriria — a religação
                            simplesmente não aconteceria.

                            Medido em 30 dias: Zabbix 5260 recuperações em 10703
                            eventos; Wazuh, 0. Não é defeito do Wazuh — `sshd` e
                            força bruta são eventos pontuais, não estados. */}
                        <Tooltip
                          label={
                            !item.tem_recuperacao
                              ? 'Esta fonte não avisa quando o problema acaba, então a religação nunca pararia'
                              : 'Escolha o nível de entrada para habilitar'
                          }
                          disabled={item.tem_recuperacao && !semNivel}
                        >
                          <Box>
                            <NumberInput
                              aria-label={`Minutos para religar ${item.rotulo}`}
                              placeholder={
                                item.tem_recuperacao ? 'Não religa' : 'Indisponível'
                              }
                              min={1}
                              max={1440}
                              value={p?.reack_after_min ?? ''}
                              onChange={(v) =>
                                mexer(item.key, {
                                  reack_after_min: v === '' ? null : Number(v),
                                })
                              }
                              disabled={semNivel || !item.tem_recuperacao}
                              // Mesmo tratamento do campo vizinho (DS §3.2):
                              // número é dado, e dado é JetBrains Mono; só
                              // quando HÁ número, senão a monoespaçada cairia
                              // sobre o placeholder, que é palavra.
                              styles={{
                                input: {
                                  fontFamily:
                                    p?.reack_after_min != null
                                      ? 'var(--mantine-font-family-monospace)'
                                      : undefined,
                                  fontVariantNumeric: 'tabular-nums',
                                },
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        {/* Exceção declarada, não quarta opção do seletor: alerta
                            com três donos não tem dono. */}
                        <Tooltip
                          label="Escolha o nível de entrada para habilitar o disparo em todos os níveis"
                          disabled={!semNivel}
                        >
                          <Box w="fit-content">
                            <Switch
                              aria-label={`Disparar ${item.rotulo} em todos os níveis`}
                              checked={p?.broadcast_all ?? false}
                              onChange={(e) =>
                                mexer(item.key, { broadcast_all: e.currentTarget.checked })
                              }
                              disabled={semNivel}
                            />
                          </Box>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Group justify="flex-start" align="center" mt="md">
            {/* Antes: `Object.keys(porKey).length === 0`, que conta as
                políticas JÁ SALVAS — com 5 políticas no banco o botão nascia
                habilitado sem nenhuma edição, e clicar reescrevia o que já
                estava lá. Quem decide se há o que salvar é `edicao`. */}
            <Button
              loading={salvar.isPending}
              disabled={pendentes === 0}
              onClick={() => salvar.mutate(Object.values(porKey))}
            >
              Salvar configuração
            </Button>
            {pendentes > 0 && (
              <Text size="sm" c="var(--ai-text-secondary)">
                {pendentes === 1
                  ? '1 alteração pendente'
                  : `${pendentes} alterações pendentes`}
              </Text>
            )}
          </Group>
        </EstadoDeConsulta>
      </DataCard>
    </Stack>
  )
}
